import { PixelRatio, useWindowDimensions } from 'react-native';

/**
 * The widths the image CDN renders. Must match `IMAGE_WIDTHS` in primum-infra's
 * [`@primum/images`](https://github.com/primuminc/primum-infra/blob/main/packages/images/src/index.ts):
 * the CDN rounds any other width up to the next of these, so a width outside
 * the list costs a larger download than it asks for.
 */
export const IMAGE_WIDTHS = [64, 128, 256, 384, 640, 828, 1080, 1600, 2048] as const;

/**
 * Photos look no sharper at 3x than at 2x on a phone, and the 3x copy is about
 * twice the bytes, so density is capped here.
 */
const MAX_DENSITY = 2;

export function snapImageWidth(width: number): number {
  return IMAGE_WIDTHS.find((allowed) => allowed >= width) ?? IMAGE_WIDTHS[IMAGE_WIDTHS.length - 1];
}

/**
 * Asks the image CDN for `url` at a width that covers `displayWidth` points on
 * a screen of `pixelRatio`. Paths the CDN does not resize ignore the parameter
 * and serve the original.
 */
export function sizedImageUrl(url: string, displayWidth: number, pixelRatio: number): string {
  const width = snapImageWidth(displayWidth * Math.min(pixelRatio, MAX_DENSITY));

  return `${url}${url.includes('?') ? '&' : '?'}w=${width}`;
}

/**
 * `sizedImageUrl` for an image laid out at up to `maxDisplayWidth` points,
 * or the window's width when that is narrower.
 */
export function useSizedImageUrl(url: string | undefined, maxDisplayWidth: number) {
  const { width: windowWidth } = useWindowDimensions();

  if (!url) {
    return undefined;
  }

  return sizedImageUrl(url, Math.min(windowWidth, maxDisplayWidth), PixelRatio.get());
}
